import { TransactionType } from 'types/finance';

export type ImportRow = {
  id: string;
  date: string;
  type: TransactionType;
  amount: number;
  description: string;
  marked: boolean;
  reference?: string;
  wallet?: string;
  /** True when already present in saved transactions */
  duplicate?: boolean;
  source?: 'wallet' | 'bank' | 'manual' | 'import';
  tokenSymbol?: string;
  tokenAmount?: number;
  amountUsdt?: number;
};

export type ImportParseResult = {
  rows: ImportRow[];
  source: 'csv' | 'excel' | 'pdf' | 'wallet';
  warnings: string[];
};

export function monthKeyFromDate(date: string): string {
  return date.slice(0, 7); // YYYY-MM
}

export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-');
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const idx = Number(m) - 1;
  return `${names[idx] || m} ${y}`;
}

export function isDuplicateTransaction(
  row: Pick<ImportRow, 'reference' | 'date' | 'type' | 'amount' | 'description'>,
  existing: Array<
    Pick<
      { reference?: string; date: string; type: string; amount: number; description: string },
      'reference' | 'date' | 'type' | 'amount' | 'description'
    >
  >
): boolean {
  const ref = (row.reference || '').trim().toLowerCase();
  if (ref) {
    return existing.some((tx) => (tx.reference || '').trim().toLowerCase() === ref);
  }
  return existing.some(
    (tx) =>
      tx.date === row.date &&
      tx.type === row.type &&
      Math.abs(tx.amount - row.amount) < 0.000001 &&
      (tx.description || '').trim() === (row.description || '').trim()
  );
}

const MONTHS: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
};

export function parseAmount(raw: string): number | null {
  if (raw == null) return null;
  const cleaned = String(raw)
    .replace(/[",\s]/g, '')
    .replace(/[^\d.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.abs(n) : null;
}

/** Accepts YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, 15-JUN-26 */
export function normalizeDate(raw: string): string | null {
  if (!raw) return null;
  const s = String(raw).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, '0');
    const month = dmy[2].padStart(2, '0');
    let year = dmy[3];
    if (year.length === 2) year = Number(year) >= 70 ? `19${year}` : `20${year}`;
    return `${year}-${month}-${day}`;
  }

  const bank = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (bank) {
    const day = bank[1].padStart(2, '0');
    const mon = MONTHS[bank[2].toUpperCase()];
    if (!mon) return null;
    let year = bank[3];
    if (year.length === 2) year = Number(year) >= 70 ? `19${year}` : `20${year}`;
    return `${year}-${mon}-${day}`;
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return null;
}

export function makeImportId(index: number): string {
  return `import-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`;
}

export function detectType(value: string, amountSign?: number): TransactionType {
  const v = (value || '').toLowerCase().trim();
  if (v === 'income' || v === 'credit' || v === 'cr' || v === 'in') return 'income';
  if (v === 'expense' || v === 'debit' || v === 'dr' || v === 'out') return 'expense';
  if (amountSign != null && amountSign < 0) return 'expense';
  if (amountSign != null && amountSign > 0 && /^-/.test(String(amountSign))) return 'expense';
  return 'expense';
}
